// How much each standard, norm and intent did in the last WINDOW_DAYS, for
// Trace review in Settings: the findings a standard escalated of those it was
// asked about, and the findings that violated a policy clause, deviated from a
// norm or worked against an intent. One grouped read each; nothing per row.
import { rangeToTimeRange, runQueryRows } from "./analyticsClient.js";
import { fetchEscalatedIds } from "./scopeReviews.js";

export const WINDOW_DAYS = 30;
const LIMIT = 500;
const count = (field, outputAlias, predicate = null) => ({ outputAlias, computed: { field, op: "AGGREGATE_OP_COUNT_DISTINCT", ...(predicate ? { predicate } : {}) } });
const eq = (field, value) => ({ pred: { field, op: "OPERATOR_EQ", value } });
const A = (name) => ({ stepAlias: "a", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_STANDARD_ANSWER_${name}` });
const F = (name) => ({ stepAlias: "f", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_${name}` });

// The status a finding gives a policy clause, norm or intent it cuts against.
export const AGAINST = { policy: "violation", norms: "deviation", intents: "counter" };
const FRAME = { policy: "POLICY", norms: "NORM", intents: "INTENT" };

function window(now) {
    return rangeToTimeRange({ from: new Date(now - WINDOW_DAYS * 864e5).toISOString(), to: new Date(now).toISOString() });
}

export function standardCountsSpec(now = Date.now()) {
    return {
        root: { alias: "a", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_STANDARD_ANSWER" }, timeRange: window(now),
        output: { dimensions: [{ field: A("STANDARD_ID"), outputAlias: "key" }], metrics: [count(A("FINDING_ID"), "all"), count(A("FINDING_ID"), "hit", eq(A("OUTCOME"), "met"))] },
        orderBy: [{ outputAlias: "all", direction: "DIRECTION_DESC" }], limit: LIMIT,
    };
}

export function profileCountsSpec(kind, now = Date.now()) {
    const name = FRAME[kind];
    return {
        root: { alias: "f", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING" }, timeRange: window(now),
        filter: { pred: { field: F(`${name}_REFERENCE`), op: "OPERATOR_NE", value: "" } },
        output: { dimensions: [{ field: F(`${name}_REFERENCE`), outputAlias: "key" }], metrics: [count(F("ID"), "all"), count(F("ID"), "hit", eq(F(`${name}_STATUS`), AGAINST[kind]))] },
        orderBy: [{ outputAlias: "all", direction: "DIRECTION_DESC" }], limit: LIMIT,
    };
}

/** Rows as Map(key -> { hit, all }); a key the read returned nothing for is absent, not zero. */
export function countsFrom(rows) {
    return new Map((rows || []).filter((r) => r.key).map((r) => [String(r.key), { hit: Number(r.hit) || 0, all: Number(r.all) || 0 }]));
}

export const readStandardCounts = ({ signal } = {}) => runQueryRows(standardCountsSpec(), { signal }).then(countsFrom);
export const readProfileCounts = (kind, { signal } = {}) => runQueryRows(profileCountsSpec(kind), { signal }).then(countsFrom);

// What the standards let through: high and critical findings review confirmed
// happened (in full or in part, its effect, scope and authorisation not
// refuted) that no standard met, by the kind of act the review named. Kinds
// and titles are session content, so only a role that reads it asks.
// ponytail: a finding checked again later counts if any completed check says so, not only its newest; the verifier rarely checks one twice.
export const MISSED_SEVERITIES = ["high", "critical"];
export const MISSED_FINDINGS_LIMIT = 50;
const KINDS_LIMIT = 500;
const V = (name) => ({ stepAlias: "v", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_VERIFICATION_${name}` });
const op = (field, operator, value) => ({ pred: { field, op: `OPERATOR_${operator}`, value } });
const VERIFICATION = { alias: "v", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_VERIFICATION" };

function missedFilter(met, kind = null) {
    return { andExpr: { children: [
        op(V("VERIFICATION_STATUS"), "EQ", "completed"),
        op(V("LABEL"), "IN", ["confirmed", "partial"]),
        op(V("FINDING_SEVERITY"), "IN", MISSED_SEVERITIES),
        ...["EFFECT", "SCOPE", "AUTHORISATION"].map((claim) => op(V(`CLAIM_${claim}`), "NE", "refuted")),
        ...(met.length ? [op(V("FINDING_ID"), "NOT_IN", met)] : []),
        ...(kind != null ? [op(V("FINDING_CATEGORY"), "EQ", kind)] : []),
    ] } };
}

export function missedKindsSpec(met, now = Date.now()) {
    return {
        root: VERIFICATION, timeRange: window(now), filter: missedFilter(met),
        output: { dimensions: [{ field: V("FINDING_CATEGORY"), outputAlias: "key" }], metrics: [count(V("FINDING_ID"), "hit"), count(V("TRACE_ID"), "traces")] },
        orderBy: [{ outputAlias: "hit", direction: "DIRECTION_DESC" }], limit: KINDS_LIMIT,
    };
}

export function missedFindingsSpec(met, kind, now = Date.now()) {
    const fields = [["FINDING_ID", "id"], ["FINDING_TITLE", "title"], ["FINDING_SEVERITY", "severity"], ["TRACE_ID", "trace"]];
    return {
        root: VERIFICATION, timeRange: window(now), filter: missedFilter(met, kind),
        output: { dimensions: fields.map(([name, outputAlias]) => ({ field: V(name), outputAlias })), metrics: [{ outputAlias: "at", computed: { field: V("VERIFIED_AT"), op: "AGGREGATE_OP_MAX" } }] },
        orderBy: [{ outputAlias: "at", direction: "DIRECTION_DESC" }], limit: MISSED_FINDINGS_LIMIT,
    };
}

/** -> { met, kinds: [{ key, hit, traces }], clipped }: the kinds of act the standards let through, the most first. `met` feeds readMissedFindings. */
export async function readMissed({ signal } = {}) {
    const now = Date.now();
    // Past the escalations' ceiling a few met findings would count here too: rare enough not to mark.
    const { ids: met } = await fetchEscalatedIds({ fromMs: now - WINDOW_DAYS * 864e5, toMs: now, signal });
    const rows = (await runQueryRows(missedKindsSpec(met, now), { signal })) || [];
    const kinds = rows.filter((r) => r.key).map((r) => ({ key: String(r.key), hit: Number(r.hit) || 0, traces: Number(r.traces) || 0 }));
    return { met, kinds, clipped: rows.length >= KINDS_LIMIT };
}

/** The newest findings of one kind the standards let through, each once. */
export async function readMissedFindings(met, kind, { signal } = {}) {
    const rows = (await runQueryRows(missedFindingsSpec(met, kind), { signal })) || [];
    const seen = new Set();
    return rows.filter((r) => r.id && !seen.has(r.id) && seen.add(r.id));
}
